import { Text, View, XStack, YStack } from "tamagui";
import { RiFireLine, RiWallet3Line, RiArrowRightSLine } from "react-icons/ri";

import { colors } from "@styles/colors";
import { pointsToNtd, type PointsKind } from "@utils/points";
import { POINTS_TINT, SOFT_SHADOW } from "./pointsTint";

const ICON: Record<PointsKind, (color: string, size: number) => JSX.Element> = {
  stored_gas: (color, size) => <RiFireLine size={size} color={color} />,
  account: (color, size) => <RiWallet3Line size={size} color={color} />,
};

const LABEL: Record<PointsKind, string> = { stored_gas: "存氣點數", account: "帳戶點數" };
const HINT: Record<PointsKind, string> = { stored_gas: "限瓦斯品項", account: "全單可折" };

interface PointsBalanceCardProps {
  kind: PointsKind;
  /** 餘額（點）。loading 時不顯示數值。 */
  points: number;
  loading?: boolean;
  /** 大卡：滿版、數值更大（點數頁用）。小卡用於帳戶頁的兩欄並排。 */
  size?: "sm" | "lg";
  onPress?: () => void;
}

/**
 * 點數餘額卡。用 V2 白卡的做法（圓角 + 柔和藍陰影、不畫硬邊框），
 * 與同頁的 ListContainer / SkinCard 是同一種白卡；顏色只出現在圖示與換算金額上。
 */
const PointsBalanceCard = ({ kind, points, loading, size = "sm", onPress }: PointsBalanceCardProps) => {
  const t = POINTS_TINT[kind];
  const lg = size === "lg";

  /** 換算金額做成極淡的同色 chip：它是「這些點值多少錢」的答案，比餘額本身更接近使用者要的資訊。 */
  const amountChip = (
    <XStack ai="center" bg={t.wash} br={999} px={lg ? 10 : 8} py={lg ? 4 : 3}>
      <Text fontSize={lg ? 13 : 11} fontWeight="700" color={t.icon} style={{ fontVariantNumeric: "tabular-nums" }}>
        {loading ? "—" : `NT$ ${pointsToNtd(points).toLocaleString()}`}
      </Text>
    </XStack>
  );

  return (
    <YStack
      bg={colors.light}
      br={lg ? 20 : 16}
      px={lg ? 18 : 13}
      py={lg ? 16 : 12}
      cursor={onPress ? "pointer" : "default"}
      onPress={onPress}
      pressStyle={onPress ? { opacity: 0.8 } : undefined}
      style={{ boxShadow: SOFT_SHADOW }}>
      <XStack ai="center" gap={lg ? 9 : 7}>
        <View w={lg ? 30 : 24} h={lg ? 30 : 24} br={lg ? 9 : 7} bg={t.tile} ai="center" jc="center" flexShrink={0}>
          {ICON[kind](t.icon, lg ? 16 : 13)}
        </View>
        <Text fontSize={lg ? 14 : 12} fontWeight="700" color={colors.text}>
          {LABEL[kind]}
        </Text>
        {onPress && (
          <View ml="auto">
            <RiArrowRightSLine size={16} color={colors.hint} />
          </View>
        )}
      </XStack>

      {lg ? (
        <XStack ai="flex-end" jc="space-between" gap={12} mt={14}>
          <XStack ai="baseline" gap={4}>
            <Text fontSize={32} fontWeight="800" color={colors.text} lineHeight={34} style={{ fontVariantNumeric: "tabular-nums" }}>
              {loading ? "—" : points.toLocaleString()}
            </Text>
            <Text fontSize={14} fontWeight="600" color={colors.sub}>
              點
            </Text>
          </XStack>
          <YStack ai="flex-end" gap={4}>
            <Text fontSize={10.5} color={colors.hint}>
              可折抵 · {HINT[kind]}
            </Text>
            {amountChip}
          </YStack>
        </XStack>
      ) : (
        // 小卡（帳戶頁兩欄並排）只給餘額：換算金額在點進去的那一頁才有意義，
        // 這裡它會變成同一張卡上第二個數字，兩張卡並排就是四個數字在互相干擾。
        <XStack ai="baseline" gap={3} mt={10}>
          <Text fontSize={22} fontWeight="800" color={colors.text} lineHeight={24} style={{ fontVariantNumeric: "tabular-nums" }}>
            {loading ? "—" : points.toLocaleString()}
          </Text>
          <Text fontSize={11.5} fontWeight="600" color={colors.sub}>
            點
          </Text>
        </XStack>
      )}
    </YStack>
  );
};

export default PointsBalanceCard;
